import { useMemo } from 'react'
import { Routing } from 'uniswap/src/data/tradingApi/__generated__/index'
import { AccountMeta } from 'uniswap/src/features/accounts/types'
import { GasFeeResult } from 'uniswap/src/features/gas/types'
import { useTokenApprovalInfo } from 'uniswap/src/features/transactions/swap/hooks/useTokenApprovalInfo'
import { useTransactionRequestInfo } from 'uniswap/src/features/transactions/swap/hooks/useTransactionRequestInfo'
import { DerivedSwapInfo } from 'uniswap/src/features/transactions/swap/types/derivedSwapInfo'
import {
  SwapGasFeeEstimation,
  SwapTxAndGasInfo,
  UniswapXGasBreakdown,
} from 'uniswap/src/features/transactions/swap/types/swapTxAndGasInfo'
import { ApprovalAction, Trade } from 'uniswap/src/features/transactions/swap/types/trade'
import { sumGasFees } from 'uniswap/src/features/transactions/swap/utils/gas'
import { isUniswapX } from 'uniswap/src/features/transactions/swap/utils/routing'
import { validatePermit, validateTransactionRequest } from 'uniswap/src/features/transactions/swap/utils/trade'
import { CurrencyField } from 'uniswap/src/types/currency'
import { isInterface } from 'utilities/src/platform'

export function useSwapTxAndGasInfo({
  derivedSwapInfo,
  account,
}: {
  derivedSwapInfo: DerivedSwapInfo
  account?: AccountMeta
}): SwapTxAndGasInfo {
  const {
    chainId,
    wrapType,
    currencyAmounts,
    trade: { trade, indicativeTrade },
  } = derivedSwapInfo

  const tokenApprovalInfo = useTokenApprovalInfo({
    account,
    chainId,
    wrapType,
    currencyInAmount: currencyAmounts[CurrencyField.INPUT],
    currencyOutAmount: currencyAmounts[CurrencyField.OUTPUT],
    routing: trade?.routing,
  })

  // TODO(MOB-3425) decouple wrap tx from swap tx to simplify UniswapX code
  // 🔧 修复：只在真正需要等待 approval 的情况下才跳过
  const hasPermitData = !!trade?.quote?.permitData
  
  // 🔧 关键修复：只有当需要链上授权时才跳过 swap txRequest
  // 不需要授权（None）或授权信息缺失（null/undefined）时，继续请求 txRequest
  const needsBlockingApproval = tokenApprovalInfo?.action === ApprovalAction.Permit2Approve ||
                                  tokenApprovalInfo?.action === ApprovalAction.RevokeAndPermit2Approve
  
  const shouldSkipSwapTx = needsBlockingApproval && hasPermitData

  const swapTxInfo = useTransactionRequestInfo({
    account,
    derivedSwapInfo,
    skip: shouldSkipSwapTx,
    tokenApprovalInfo,
  })

  return useMemo(() => {
    const gasFeeEstimation: SwapGasFeeEstimation = {
      ...swapTxInfo.gasEstimate,
      approvalEstimates: tokenApprovalInfo?.gasEstimates,
    }

    // Gas fees for: swap from quote response directly, wrap from Gas Fee API, approvals from checkApprovalQuery
    const gasFee = getTotalGasFee(trade, swapTxInfo.gasFeeResult, tokenApprovalInfo, account)

    const approveTxRequest = validateTransactionRequest(tokenApprovalInfo?.txRequest)
    const revocationTxRequest = validateTransactionRequest(tokenApprovalInfo?.cancelTxRequest)
    const txRequest = validateTransactionRequest(swapTxInfo.transactionRequest)
    const permit = validatePermit(swapTxInfo.permitData)
    const unsigned = Boolean(isInterface && swapTxInfo.permitData)
    

    if (trade?.routing === Routing.DUTCH_V2 || trade?.routing === Routing.PRIORITY) {
      const signature = swapTxInfo.permitSignature
      const orderParams = signature ? { signature, quote: trade.quote.quote, routing: Routing.DUTCH_V2 } : undefined
      const gasFeeBreakdown: UniswapXGasBreakdown = {
        classicGasUseEstimateUSD: trade.quote.quote.classicGasUseEstimateUSD,
        approvalCost: tokenApprovalInfo?.displayGasFee,
        wrapCost: swapTxInfo.gasFeeResult.displayValue,
        inputTokenSymbol: trade.inputAmount.currency.wrapped.symbol,
      }

      return {
        routing: trade.routing,
        trade,
        indicativeTrade,
        wrapTxRequest: txRequest,
        approveTxRequest,
        revocationTxRequest,
        orderParams,
        gasFee,
        gasFeeEstimation,
        gasFeeBreakdown,
        permit,
      }
    } else if (trade?.routing === Routing.BRIDGE) {
      return {
        routing: Routing.BRIDGE,
        trade,
        indicativeTrade: undefined, // Bridge trades don't have indicative trades
        txRequest,
        approveTxRequest,
        revocationTxRequest,
        gasFee,
        gasFeeEstimation,
        swapRequestArgs: swapTxInfo.swapRequestArgs,
        permit,
        unsigned,
      }
    } else {
      return {
        routing: Routing.CLASSIC,
        trade: trade ?? undefined,
        indicativeTrade,
        txRequest,
        approveTxRequest,
        revocationTxRequest,
        gasFee,
        gasFeeEstimation,
        swapRequestArgs: swapTxInfo.swapRequestArgs,
        permit,
        unsigned,
      }
    }
  }, [
    account,
    indicativeTrade,
    swapTxInfo.gasEstimate,
    swapTxInfo.gasFeeResult,
    swapTxInfo.permitSignature,
    swapTxInfo.transactionRequest,
    swapTxInfo.permitData,
    swapTxInfo.swapRequestArgs,
    tokenApprovalInfo,
    trade,
  ])
}

type TokenApprovalInfoWithGas = ReturnType<typeof useTokenApprovalInfo>
function getTotalGasFee(
  trade: Trade | null,
  swapGasResult: GasFeeResult,
  tokenApprovalInfo: TokenApprovalInfoWithGas,
  account?: AccountMeta,
): GasFeeResult {
  const isConnected = !!account?.address
  const blockingUnknownApprovalStatus = isConnected && tokenApprovalInfo.action === ApprovalAction.Unknown
  const isLoading = swapGasResult.isLoading || (blockingUnknownApprovalStatus && tokenApprovalInfo.isLoading)

  const approvalError =
    blockingUnknownApprovalStatus && !tokenApprovalInfo.isLoading ? new Error('Approval action unknown') : null
  let error = swapGasResult.error ?? approvalError

  // If swap requires revocation we expect simulation error so set error to null
  if (tokenApprovalInfo?.action === ApprovalAction.RevokeAndPermit2Approve) {
    error = null
  }

  const isGaslessSwap = trade && isUniswapX(trade) && !trade.needsWrap
  const noApprovalNeeded = !isConnected || tokenApprovalInfo?.action === ApprovalAction.None
  const approvalGasFeeMissing = !tokenApprovalInfo
  const swapGasFeeMissing = !swapGasResult.value && !isGaslessSwap

  // For UniswapX orders with no wrap and no approval, show total gas fee as 0.
  if (isGaslessSwap && noApprovalNeeded) {
    return { value: '0', displayValue: '0', error, isLoading }
  }

  // If user is disconnected, we don't have approval info, so use swapGasResult only for gas estimation
  if (!isConnected && !swapGasFeeMissing) {
    return swapGasResult
  }

  // 🔧 修复：当需要授权时，允许只有 approval gas fee 而没有 swap gas fee
  const needsApproval = tokenApprovalInfo?.action === ApprovalAction.Permit2Approve || 
                        tokenApprovalInfo?.action === ApprovalAction.RevokeAndPermit2Approve
  
  // 🔧 关键修复：如果只需要授权（还没有 swap），只返回授权的 gas fee
  if (needsApproval && tokenApprovalInfo?.gasFee && swapGasFeeMissing) {
    const value = sumGasFees([tokenApprovalInfo.gasFee, tokenApprovalInfo.cancelGasFee])
    const displayValue = sumGasFees([
      tokenApprovalInfo.displayGasFee,
      tokenApprovalInfo.displayCancelGasFee,
    ])
    return { value, displayValue, error: null, isLoading }
  }

  // 🔧 关键修复：如果不需要授权（approval === null），只需要 swap gas fee
  if (noApprovalNeeded) {
    if (!swapGasFeeMissing) {
      // 有 swap gas fee，直接返回
      return swapGasResult
    } else {
      // 没有 swap gas fee，返回 loading 状态（不阻止按钮）
      return { value: undefined, displayValue: undefined, error, isLoading: true }
    }
  }

  // Do not populate gas fee:
  // - If errors exist on swap or approval requests.
  // - If we don't have both the approval and transaction gas fees (when approval is needed).
  // 🔧 修复：如果不需要授权，不检查 approvalGasFeeMissing
  const shouldCheckApprovalGas = !noApprovalNeeded && approvalGasFeeMissing
  
  if (shouldCheckApprovalGas || swapGasFeeMissing || blockingUnknownApprovalStatus || error) {
    return { value: undefined, displayValue: undefined, error, isLoading }
  }

  // 🔧 修复：合并 approval 和 swap gas fees（如果都存在）
  const value = sumGasFees([swapGasResult.value, tokenApprovalInfo.gasFee, tokenApprovalInfo.cancelGasFee])
  const displayValue = sumGasFees([
    swapGasResult.displayValue,
    tokenApprovalInfo.displayGasFee,
    tokenApprovalInfo.displayCancelGasFee,
  ])
  return { value, displayValue, error, isLoading }
}
